import {isNotNullOrUndefined} from './copilot-chat-entitlement'
import {TBB_QUOTA_TRIGGER} from './copilot-chat-helpers'
import {
  type CopilotChatEntitlementQuotas,
  CopilotPlan,
  type QuotaLimitData,
  UserLevelBudgetState,
} from './copilot-chat-types'
import {copilotFeatureFlags} from './copilot-feature-flags'

interface TbbStateInput {
  quotas: CopilotChatEntitlementQuotas | undefined
  plan: CopilotPlan | null
  hasCEorCBAccess: boolean
  isLicensedLimited: boolean
}

export interface TbbState {
  overagesEnabled: boolean
  userLevelBudgetEnabled: boolean
  userLevelBudgetState: UserLevelBudgetState | null
  userLevelBudgetPercentRemaining: number | null
  quotaRemaining: number | undefined
  quotaExceeded: boolean
  quotaResetAt: number | undefined | null
  overagesRemaining: number | undefined
  overagesExceeded: boolean
  tbbQuotaExceeded: boolean
  isIndividualPaidOrStudentPlan: boolean
  isCommercialPlan: boolean
}

function getUserLevelBudgetState(
  userLevelBudgetEnabled: boolean,
  piQuota: QuotaLimitData | undefined,
  overagesEnabled: boolean,
): UserLevelBudgetState | null {
  if (!userLevelBudgetEnabled || !piQuota) return null
  const unlimited = !!piQuota.unlimited
  const hasQuota = piQuota.hasQuota !== false
  // Order matters: usage-blocked and no-limit both have unlimited truthy.
  if (unlimited && !hasQuota) return UserLevelBudgetState.UsageBlocked
  if (unlimited) return UserLevelBudgetState.NoLimit
  if (piQuota.percentRemaining <= 0 && !overagesEnabled) return UserLevelBudgetState.BudgetReached
  if (piQuota.percentRemaining <= TBB_QUOTA_TRIGGER) return UserLevelBudgetState.BudgetProgress
  return null
}

function getTbbQuotaExceeded(
  plan: CopilotPlan | null,
  piQuota: QuotaLimitData | undefined,
  quotaExceeded: boolean,
  overagesEnabled: boolean,
  overagesExceeded: boolean,
): boolean {
  if (!copilotFeatureFlags.copilotDotcomChatTbb) return false
  if (plan === CopilotPlan.Business || plan === CopilotPlan.Enterprise) {
    return !!piQuota && !piQuota.unlimited && piQuota.percentRemaining <= 0
  }
  const quotaExceededButOveragesNotAllowed = quotaExceeded && !overagesEnabled
  const overagesQuotaExceeded = overagesEnabled && quotaExceeded && overagesExceeded
  return quotaExceededButOveragesNotAllowed || overagesQuotaExceeded
}

// Selects the appropriate quota source based on user type:
// - Free/limited users → chatQuota
// - Paid individual users → premiumInteractionsQuota
function getQuotaForUser(
  quotas: CopilotChatEntitlementQuotas | undefined,
  isLicensedLimited: boolean,
): QuotaLimitData | undefined {
  if (isLicensedLimited) {
    return quotas?.chatQuota
  }
  return quotas?.premiumInteractionsQuota
}

export function getTbbEntitlement({quotas, plan, hasCEorCBAccess, isLicensedLimited}: TbbStateInput): TbbState {
  const overagesEnabled =
    (copilotFeatureFlags.copilotDotcomChatTbb ? quotas?.overageQuota?.permitted : quotas?.overagesEnabled) ?? false

  const userLevelBudgetEnabled =
    copilotFeatureFlags.copilotDotcomChatTbb && !!quotas?.tokenBasedBillingEnabled && hasCEorCBAccess

  const piQuota = quotas?.premiumInteractionsQuota
  const userLevelBudgetState = getUserLevelBudgetState(userLevelBudgetEnabled, piQuota, overagesEnabled)
  const userLevelBudgetPercentRemaining = userLevelBudgetEnabled && piQuota ? piQuota.percentRemaining : null

  // TBB Quota: free users use chatQuota, paid individual users use premiumInteractionsQuota
  const userQuota = getQuotaForUser(quotas, isLicensedLimited)
  const quotaRemaining = userQuota?.percentRemaining
  const quotaExceeded = isNotNullOrUndefined(quotaRemaining) && quotaRemaining <= 0
  const quotaResetAt = userQuota?.resetAt
  const overagesRemaining = quotas?.overageQuota?.percentRemaining
  const overagesExceeded = isNotNullOrUndefined(overagesRemaining) && overagesRemaining <= 0

  const isIndividualPaidOrStudentPlan =
    plan === CopilotPlan.IndividualPro ||
    plan === CopilotPlan.IndividualStudent ||
    plan === CopilotPlan.IndividualProPlus ||
    plan === CopilotPlan.IndividualMax
  const isCommercialPlan = plan === CopilotPlan.Enterprise || plan === CopilotPlan.Business

  const tbbQuotaExceeded = getTbbQuotaExceeded(plan, piQuota, quotaExceeded, overagesEnabled, overagesExceeded)

  return {
    overagesEnabled,
    userLevelBudgetEnabled,
    userLevelBudgetState,
    userLevelBudgetPercentRemaining,
    quotaRemaining,
    quotaExceeded,
    quotaResetAt,
    overagesRemaining,
    overagesExceeded,
    tbbQuotaExceeded,
    isIndividualPaidOrStudentPlan,
    isCommercialPlan,
  }
}
